import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import {
  SalesTaxConfig,
  SalesTaxExemption,
  SalesTaxExemptionService,
  SalesTaxExemptionType,
  SalesTaxReviewWarning,
  SaveSalesTaxExemptionRequest,
  SALES_TAX_ACCEPTED_MIME_TYPES,
  SALES_TAX_MAX_UPLOAD_BYTES,
} from '../../service-data/sales-tax-exemption.service';
import { normalizeSalesTaxStateCodes, SALES_TAX_EXEMPTION_STATE_CODES, US_STATES } from '../../util/us-states';
import { LanguagePipe } from '../../pipe/language.pipe';
import { Organization } from '../../../model/Organization';

/**
 * Organization-side panel for requesting / managing a Sales Tax
 * Exemption.
 *
 * Hosted by `SettingsComponent` (org-role only). Reads/writes the
 * `salesTaxExemptionData` blob via `SalesTaxExemptionService` —
 * deliberately NOT through `Organization` (that field is NOT
 * carried on the in-memory entity per the contract; see
 * docs/contracts/shared/SalesTaxExemption.md §Storage strategy).
 *
 * UI flow:
 *   1. Load the org's current exemption blob + SP's salesTaxConfig.
 *   2. Show current status (DRAFT / PENDING_REVIEW / ACCEPTED / ...).
 *   3. Allow editing while status ∈ { DRAFT, INFO_REQUESTED }
 *      (or always — server is the source of truth, this is just UX).
 *   4. Upload the certificate file via the presigned-URL flow
 *      mirroring `ProjectDraftComponent` / `ResourceService.getUploadUrl`
 *      (request URL from server → XHR PUT to S3 → record fileKey).
 *   5. Pick the exempt states via `<app-sales-tax-states-matrix>`.
 *   6. Save → `PUT /salestax/exemption/:orgId` → server transitions
 *      status to PENDING_REVIEW.
 */
@Component({
  selector: 'app-sales-tax-exemption-organization-panel',
  templateUrl: './sales-tax-exemption-organization-panel.component.html',
  styleUrls: ['./sales-tax-exemption-organization-panel.component.css'],
})
export class SalesTaxExemptionOrganizationPanelComponent implements OnInit, OnChanges {
  @Input() organization: Organization;

  exemption: SalesTaxExemption | null = null;
  config: SalesTaxConfig | null = null;

  loading = false;
  loadError = '';
  saving = false;
  saveError = '';
  saveMessage = '';
  /** Non-blocking warnings from the most recent save (e.g. downstream sales-tax service sync failed). */
  saveWarnings: SalesTaxReviewWarning[] = [];

  // Editable working copies
  editing = false;
  workingType: SalesTaxExemptionType = 'non-exempt';
  workingCertNumber = '';
  workingIssuingState = '';
  workingExemptStates: string[] = [];
  workingMessage = '';
  /** Either an already-uploaded fileKey from a prior save, or a fresh key from a just-completed upload. */
  workingFileKey = '';
  workingFileMimeType = '';
  workingFileSize = 0;
  workingFileName = '';

  // Upload state
  uploadState: 'IDLE' | 'UPLOADING' | 'UPLOADED' | 'ERROR' = 'IDLE';
  uploadProgress = 0;
  uploadError = '';

  /** All US states/territories accepted by the contract. */
  readonly stateOptions = SALES_TAX_EXEMPTION_STATE_CODES
    .map(c => US_STATES.find(s => s.code === c))
    .filter((s): s is { code: string; name: string } => !!s)
    .sort((a, b) => a.name.localeCompare(b.name));

  readonly acceptedMimeTypes = SALES_TAX_ACCEPTED_MIME_TYPES.join(',');
  readonly maxUploadBytes = SALES_TAX_MAX_UPLOAD_BYTES;

  constructor(private salesTaxExemptionService: SalesTaxExemptionService) {}

  ngOnInit() {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.organization && this.organization) {
      this.refresh();
    }
  }

  get orgId(): number | null {
    return this.organization ? this.organization.ID : null;
  }

  get spId(): number | null {
    return this.organization ? this.organization.serviceProvider_ID : null;
  }

  get canEdit(): boolean {
    if (!this.exemption) { return true; }
    return this.exemption.status === 'DRAFT'
        || this.exemption.status === 'INFO_REQUESTED'
        || this.exemption.status === 'REJECTED';
  }

  get requiresCertificate(): boolean {
    return this.workingType === 'wholesale' || this.workingType === 'other';
  }

  get hasUsableCertificate(): boolean {
    return !!this.workingFileKey;
  }

  get readyToSave(): boolean {
    if (this.saving) { return false; }
    if (this.workingType === 'non-exempt') { return true; }
    return !!this.workingCertNumber
        && !!this.workingIssuingState
        && this.hasUsableCertificate;
  }

  refresh() {
    if (!this.orgId) { return; }
    this.loading = true;
    this.loadError = '';
    const orgId = this.orgId;
    // Load both in parallel; config is best-effort (org-side read).
    this.salesTaxExemptionService.getExemption(orgId).subscribe(
      ex => {
        this.exemption = ex;
        this.seedWorkingCopiesFromExemption();
        this.loading = false;
      },
      err => {
        console.error('SalesTaxExemptionOrganizationPanel: getExemption failed', err);
        this.loading = false;
        this.loadError = this.errorMessage(err,
          LanguagePipe.get('Failed to load Sales Tax Exemption.'));
      }
    );
    if (this.spId != null) {
      this.salesTaxExemptionService.getSpConfig(this.spId).subscribe(
        cfg => { this.config = cfg; },
        err => {
          // Non-fatal — the matrix will degrade to no reciprocity/nexus styling.
          console.warn('SalesTaxExemptionOrganizationPanel: getSpConfig failed', err);
        }
      );
    }
  }

  startEdit() {
    this.editing = true;
    this.seedWorkingCopiesFromExemption();
    // When the user explicitly clicks "Request Exemption" on an org with no
    // exemption on file, default the type to 'wholesale' so the certificate
    // fields and the state-matrix selector are visible immediately. The
    // 'non-exempt' default only makes sense for the rest-state view, not
    // for someone who has just clicked "Request Exemption".
    if (!this.exemption) {
      this.workingType = 'wholesale';
    }
    this.saveError = '';
    this.saveMessage = '';
  }

  cancelEdit() {
    this.editing = false;
    this.uploadState = 'IDLE';
    this.uploadError = '';
    this.uploadProgress = 0;
    this.seedWorkingCopiesFromExemption();
  }

  // ── File upload ────────────────────────────────────────────────────

  onFileSelected(input: HTMLInputElement): void {
    const file = input.files && input.files[0];
    if (!file) { return; }
    this.uploadError = '';
    this.uploadProgress = 0;

    if (SALES_TAX_ACCEPTED_MIME_TYPES.indexOf(file.type) === -1) {
      this.uploadState = 'ERROR';
      this.uploadError = LanguagePipe.get('Unsupported file type. Use PNG, JPEG, GIF, or PDF.');
      input.value = '';
      return;
    }
    if (file.size > SALES_TAX_MAX_UPLOAD_BYTES) {
      this.uploadState = 'ERROR';
      this.uploadError = LanguagePipe.get('File is too large. Maximum size is 10 MB.');
      input.value = '';
      return;
    }
    if (!this.orgId) { return; }

    this.uploadState = 'UPLOADING';
    this.uploadProgress = 0;

    this.salesTaxExemptionService.requestUploadUrl(this.orgId, file).subscribe(
      r => {
        if (!r || !r.uploadUrl) {
          this.uploadState = 'ERROR';
          this.uploadError = LanguagePipe.get('Failed to obtain upload URL from server.');
          return;
        }
        this.salesTaxExemptionService.uploadCertificate(r.uploadUrl, file, r.requiredHeaders).subscribe(
          ev => {
            if (ev.type === HttpEventType.UploadProgress && ev.total) {
              this.uploadProgress = Math.round((ev.loaded / ev.total) * 100);
            } else if (ev.type === HttpEventType.Response) {
              this.uploadState = 'UPLOADED';
              this.uploadProgress = 100;
              this.workingFileKey = r.fileKey;
              this.workingFileMimeType = file.type;
              this.workingFileSize = file.size;
              this.workingFileName = file.name;
            }
          },
          err => {
            console.error('SalesTaxExemptionOrganizationPanel: S3 PUT failed', err);
            this.uploadState = 'ERROR';
            this.uploadError = LanguagePipe.get('Certificate upload failed. Please try again.');
          }
        );
      },
      err => {
        console.error('SalesTaxExemptionOrganizationPanel: requestUploadUrl failed', err);
        this.uploadState = 'ERROR';
        this.uploadError = this.errorMessage(err,
          LanguagePipe.get('Failed to obtain upload URL from server.'));
      }
    );

    // Reset the input so the same file can be re-selected if needed.
    input.value = '';
  }

  openCurrentCertificate(): void {
    if (this.exemption && this.exemption.certificateUrl) {
      window.open(this.exemption.certificateUrl, '_blank');
      return;
    }
    if (!this.orgId) { return; }
    this.salesTaxExemptionService.requestDownloadUrl(this.orgId).subscribe(
      r => { if (r && r.downloadUrl) { window.open(r.downloadUrl, '_blank'); } },
      err => {
        console.error('SalesTaxExemptionOrganizationPanel: requestDownloadUrl failed', err);
        this.loadError = this.errorMessage(err,
          LanguagePipe.get('Failed to obtain certificate download URL.'));
      }
    );
  }

  isCertificateImage(): boolean {
    const mt = this.exemption && this.exemption.certificate
      && this.exemption.certificate.fileMimeType;
    return !!(mt && mt.indexOf('image/') === 0);
  }

  // ── Save ───────────────────────────────────────────────────────────

  save(): void {
    if (!this.orgId || !this.readyToSave) { return; }
    this.saving = true;
    this.saveError = '';
    this.saveMessage = '';
    this.saveWarnings = [];

    const body: SaveSalesTaxExemptionRequest = {
      type: this.workingType,
      certificate: this.workingType === 'non-exempt' ? null : {
        number: this.workingCertNumber,
        issuingState: this.workingIssuingState,
        fileKey: this.workingFileKey,
        fileMimeType: this.workingFileMimeType
          || (this.exemption && this.exemption.certificate ? this.exemption.certificate.fileMimeType : ''),
        fileSize: this.workingFileSize
          || (this.exemption && this.exemption.certificate ? this.exemption.certificate.fileSize : 0),
        fileName: this.workingFileName
          || (this.exemption && this.exemption.certificate ? this.exemption.certificate.fileName : ''),
      },
      exemptStates: this.exemptStatesForSave(),
      message: this.workingMessage || undefined,
    };

    this.salesTaxExemptionService.saveExemption(this.orgId, body).subscribe(
      resp => {
        this.saving = false;
        this.editing = false;
        this.exemption = resp ? resp.exemption : null;
        this.saveWarnings = (resp && resp.warnings) ? resp.warnings : [];
        this.seedWorkingCopiesFromExemption();
        // Tailor confirmation based on the server-returned status. Non-exempt
        // is auto-ACCEPTED with immediate downstream sales-tax service
        // registration; wholesale/other go to PENDING_REVIEW.
        if (this.exemption && this.exemption.status === 'ACCEPTED' && this.exemption.type === 'non-exempt') {
          this.saveMessage = LanguagePipe.get('Registered as non-exempt with your service provider\'s sales-tax service.');
        } else if (this.exemption && this.exemption.status === 'PENDING_REVIEW') {
          this.saveMessage = LanguagePipe.get('Sales Tax Exemption submitted for review.');
        } else {
          this.saveMessage = LanguagePipe.get('Sales Tax Exemption updated.');
        }
      },
      err => {
        console.error('SalesTaxExemptionOrganizationPanel: saveExemption failed', err);
        this.saving = false;
        this.saveError = this.errorMessage(err,
          LanguagePipe.get('Failed to save Sales Tax Exemption.'));
      }
    );
  }

  // ── helpers ────────────────────────────────────────────────────────

  /**
   * States submitted with the request.
   *
   * The certificate's issuing state is always part of the claim — the matrix
   * locks it on and it cannot be unchecked while a certificate is on file
   * (docs/contracts/shared/SalesTaxExemption.md §State-matrix UX). Union it in
   * here as well so the submitted set is correct even if the matrix never
   * rendered (e.g. the user picked an issuing state and saved immediately).
   */
  private exemptStatesForSave(): string[] {
    if (this.workingType === 'non-exempt') { return []; }
    const codes = (this.workingExemptStates || []).slice();
    if (this.workingIssuingState) { codes.push(this.workingIssuingState); }
    return normalizeSalesTaxStateCodes(codes);
  }

  statusLabel(): string {
    if (!this.exemption) {
      return LanguagePipe.get('No exemption on file');
    }
    return LanguagePipe.get(this.exemption.status);
  }

  typeLabel(t: SalesTaxExemptionType): string {
    return LanguagePipe.get(t);
  }

  private seedWorkingCopiesFromExemption(): void {
    const ex = this.exemption;
    if (!ex) {
      this.workingType = 'non-exempt';
      this.workingCertNumber = '';
      this.workingIssuingState = '';
      this.workingExemptStates = [];
      this.workingFileKey = '';
      this.workingFileMimeType = '';
      this.workingFileSize = 0;
      this.workingFileName = '';
      this.workingMessage = '';
      return;
    }
    this.workingType = ex.type || 'non-exempt';
    this.workingExemptStates = (ex.exemptStates || []).slice();
    if (ex.certificate) {
      this.workingCertNumber = ex.certificate.number || '';
      this.workingIssuingState = ex.certificate.issuingState || '';
      this.workingFileKey = ex.certificate.fileKey || '';
      this.workingFileMimeType = ex.certificate.fileMimeType || '';
      this.workingFileSize = ex.certificate.fileSize || 0;
      this.workingFileName = ex.certificate.fileName || '';
    } else {
      this.workingCertNumber = '';
      this.workingIssuingState = '';
      this.workingFileKey = '';
      this.workingFileMimeType = '';
      this.workingFileSize = 0;
      this.workingFileName = '';
    }
    this.workingMessage = '';
  }

  private errorMessage(err: any, fallback: string): string {
    if (err && err.error && typeof err.error === 'object' && err.error.error) {
      return String(err.error.error);
    }
    if (err && err.message) { return String(err.message); }
    return fallback;
  }
}







